/**
 * TouchController — mobile input source implementing `ShipInputSource`.
 *
 * Driven by Pointer Events (works for touch + pen + mouse uniformly). All
 * controls are DOM elements positioned over the WebGL canvas; this class
 * only owns the *pointer state*, not the pixels — the visuals are owned by
 * `ui/TouchControlsOverlay.ts` and can be restyled without touching here.
 *
 * Layout (see docs/phase-1-plan.md §12.2):
 *   - Left virtual stick (anchored bottom-left): pitch (Y) + yaw (X)
 *   - Right virtual stick (anchored bottom-right): roll (X) + throttle target (Y)
 *   - Thrust button (bottom-center, hold): boolean thrust
 *   - Ping button (right side): edge-triggered ping
 *   - Map button (right side, top): edge-triggered toggleMap
 *
 * Each stick re-anchors to wherever you first touched within its zone, so
 * the player never has to look down. We track touches by `pointerId` so
 * left + right + thrust can all be active simultaneously.
 *
 * Routing of user actions through the codebase:
 *
 *   pointerdown / pointermove / pointerup / pointercancel
 *     -> this.pointers updated per pointerId
 *     -> sample() -> ShipInput
 *        -> game/LocalSim.ts and net/NetClient.ts
 */

import {
	type ShipInput,
	type ShipInputSource,
	clampAxis,
	clampUnit,
	zeroShipInput,
} from "@squid/shared";

/** Maximum drag distance (in CSS pixels) for a virtual stick to register
 *  as a saturated +/- 1 deflection. */
const STICK_RADIUS_PX = 64;

interface StickState {
	pointerId: number;
	originX: number;
	originY: number;
	dx: number;
	dy: number;
}

interface ButtonState {
	pointerId: number;
	pressed: boolean;
	consumed: boolean;
}

export interface TouchControlElements {
	leftStickZone: HTMLElement;
	leftStickKnob: HTMLElement;
	rightStickZone: HTMLElement;
	rightStickKnob: HTMLElement;
	thrustButton: HTMLElement;
	pingButton: HTMLElement;
	mapButton: HTMLElement;
}

export class TouchController implements ShipInputSource {
	private leftStick: StickState | null = null;
	private rightStick: StickState | null = null;
	private thrust: ButtonState = { pointerId: -1, pressed: false, consumed: false };
	private pingPending = false;
	private mapPending = false;
	private readonly out: ShipInput = zeroShipInput();
	private readonly cleanupFns: Array<() => void> = [];
	private readonly elements: TouchControlElements;

	constructor(elements: TouchControlElements) {
		this.elements = elements;

		this.bindStick(elements.leftStickZone, elements.leftStickKnob, "left");
		this.bindStick(elements.rightStickZone, elements.rightStickKnob, "right");

		this.bindHoldButton(elements.thrustButton, this.thrust);

		this.bindEdgeButton(elements.pingButton, () => {
			this.pingPending = true;
		});
		this.bindEdgeButton(elements.mapButton, () => {
			this.mapPending = true;
		});

		// Suppress browser scrolling and gestures over the controls.
		for (const el of [
			elements.leftStickZone,
			elements.rightStickZone,
			elements.thrustButton,
			elements.pingButton,
			elements.mapButton,
		]) {
			el.style.touchAction = "none";
			el.style.userSelect = "none";
		}
	}

	sample(): ShipInput {
		const left = this.leftStick;
		const right = this.rightStick;

		const pitch = left ? clampAxis(-left.dy / STICK_RADIUS_PX) : 0;
		const yaw = left ? clampAxis(left.dx / STICK_RADIUS_PX) : 0;
		const roll = right ? clampAxis(right.dx / STICK_RADIUS_PX) : 0;
		// Right stick Y target: full up = throttle 1.0, full down = throttle 0.
		// On release, throttle decays below as a separate effect.
		let throttle = right ? clampUnit(0.5 - right.dy / (STICK_RADIUS_PX * 2)) : -1;
		if (throttle < 0) {
			// No active right stick -> hold last throttle target instead of
			// snapping to zero, so a player can coast without holding the stick.
			throttle = this.out.throttle;
		}

		this.out.throttle = throttle;
		this.out.pitch = pitch;
		this.out.yaw = yaw;
		this.out.roll = roll;
		this.out.thrust = this.thrust.pressed;
		this.out.ping = this.pingPending;
		this.out.toggleMap = this.mapPending;
		this.pingPending = false;
		this.mapPending = false;
		return this.out;
	}

	dispose(): void {
		for (const fn of this.cleanupFns) fn();
		this.cleanupFns.length = 0;
		this.leftStick = null;
		this.rightStick = null;
	}

	private bindStick(
		zone: HTMLElement,
		knob: HTMLElement,
		which: "left" | "right",
	): void {
		const onDown = (ev: PointerEvent): void => {
			ev.preventDefault();
			const rect = zone.getBoundingClientRect();
			const state: StickState = {
				pointerId: ev.pointerId,
				originX: ev.clientX - rect.left,
				originY: ev.clientY - rect.top,
				dx: 0,
				dy: 0,
			};
			if (which === "left") this.leftStick = state;
			else this.rightStick = state;
			zone.setPointerCapture(ev.pointerId);
			this.updateKnob(knob, state);
		};
		const onMove = (ev: PointerEvent): void => {
			const state = which === "left" ? this.leftStick : this.rightStick;
			if (!state || state.pointerId !== ev.pointerId) return;
			const rect = zone.getBoundingClientRect();
			state.dx = clampStick(ev.clientX - rect.left - state.originX);
			state.dy = clampStick(ev.clientY - rect.top - state.originY);
			this.updateKnob(knob, state);
		};
		const onUp = (ev: PointerEvent): void => {
			const state = which === "left" ? this.leftStick : this.rightStick;
			if (!state || state.pointerId !== ev.pointerId) return;
			if (which === "left") this.leftStick = null;
			else this.rightStick = null;
			knob.style.transform = "translate(0px, 0px)";
		};
		zone.addEventListener("pointerdown", onDown);
		zone.addEventListener("pointermove", onMove);
		zone.addEventListener("pointerup", onUp);
		zone.addEventListener("pointercancel", onUp);
		this.cleanupFns.push(() => {
			zone.removeEventListener("pointerdown", onDown);
			zone.removeEventListener("pointermove", onMove);
			zone.removeEventListener("pointerup", onUp);
			zone.removeEventListener("pointercancel", onUp);
		});
	}

	private updateKnob(knob: HTMLElement, state: StickState): void {
		knob.style.transform = `translate(${state.dx}px, ${state.dy}px)`;
	}

	private bindHoldButton(el: HTMLElement, state: ButtonState): void {
		const onDown = (ev: PointerEvent): void => {
			ev.preventDefault();
			state.pointerId = ev.pointerId;
			state.pressed = true;
			el.setPointerCapture(ev.pointerId);
			el.classList.add("active");
			vibrate(10);
		};
		const onUp = (ev: PointerEvent): void => {
			if (state.pointerId !== ev.pointerId) return;
			state.pressed = false;
			el.classList.remove("active");
		};
		el.addEventListener("pointerdown", onDown);
		el.addEventListener("pointerup", onUp);
		el.addEventListener("pointercancel", onUp);
		this.cleanupFns.push(() => {
			el.removeEventListener("pointerdown", onDown);
			el.removeEventListener("pointerup", onUp);
			el.removeEventListener("pointercancel", onUp);
		});
	}

	private bindEdgeButton(el: HTMLElement, onPress: () => void): void {
		const handler = (ev: PointerEvent): void => {
			ev.preventDefault();
			onPress();
			vibrate(5);
		};
		el.addEventListener("pointerdown", handler);
		this.cleanupFns.push(() => {
			el.removeEventListener("pointerdown", handler);
		});
	}
}

function clampStick(v: number): number {
	if (v > STICK_RADIUS_PX) return STICK_RADIUS_PX;
	if (v < -STICK_RADIUS_PX) return -STICK_RADIUS_PX;
	return v;
}

function vibrate(ms: number): void {
	const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }) : undefined;
	if (nav?.vibrate) {
		try {
			nav.vibrate(ms);
		} catch {
			// ignore; some browsers throw inside iframes / lockdown mode
		}
	}
}
